import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { sqDurationStore } from '@/core/core.stores';
import { AnyProperty } from '@/utilities.types';
import { Capsule } from '@/utilities/datetime.constants';
import _ from 'lodash';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';

export class ClusteringStore extends BaseToolStore {
  static readonly storeName = 'sqClusteringStore';
  type = TREND_TOOLS.CLUSTERING;

  parameterDefinitions = {
    inputSignals: {
      predicate: (parameter: { name: string }) => !_.includes(['condition', 'restrictToCondition'], parameter.name),
      multiple: true,
    },
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
  };

  initialize() {
    this.state = this.immutable({
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      color: '',
      numClusters: 3,
    });
  }

  get inputSignals() {
    return this.state.get('inputSignals');
  }

  get condition() {
    return this.state.get('condition');
  }

  get restrictToCondition(): any {
    return this.state.get('restrictToCondition');
  }

  get window(): { startTime: number; endTime: number } {
    return this.state.get('window');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get color(): string {
    return this.state.get('color');
  }

  get numClusters(): number {
    return this.state.get('numClusters');
  }

  dehydrate() {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: AnyProperty) {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }) {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }) {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setColor(payload: { color: string }) {
    this.state.set('color', payload.color);
  }

  setNumClusters(payload: { numClusters: number }) {
    this.state.set('numClusters', payload.numClusters);
  }

  modifyConfigParams(config: any) {
    return _.omit(config, ['modelPropertiesExpanded']);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    CLUSTERING_SET_TRAINING_WINDOW: this.setTrainingWindow,
    CLUSTERING_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    CLUSTERING_SET_COLOR: this.setColor,
    CLUSTERING_SET_NUM_CLUSTERS: this.setNumClusters,
  };
}
